import React from 'react';
import { Link } from 'react-router-dom';
import {
  Cpu,
  Truck,
  Wrench,
  Award,
  ArrowRight,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { SeoHead } from '../../components/seo/seo-head';

export const NosotrosPage: React.FC = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 space-y-16">
      <SeoHead
        title="Sobre Nosotros | Trayectoria, Soporte Técnico y Distribución B2B"
        description="Conoce más sobre BARTECH - Tecnología e Ingeniería SpA. Especialistas en distribución de hardware para retail, captura de datos, RFID y soporte técnico en Chile."
        canonicalUrl="https://bartech.cl/nosotros"
      />
      {/* Hero Corporativo */}
      <section className="text-center max-w-3xl mx-auto space-y-4">
        <Badge variant="brand" size="md">
          Trayectoria & Respaldo
        </Badge>
        <h1 className="text-3xl sm:text-5xl font-black text-neutral-900 dark:text-white tracking-tight">
          Ingeniería de Hardware para el Comercio Chileno
        </h1>
        <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed">
          En BARTECH integramos equipamiento comercial, automatización de puntos de venta y tecnologías de captura de datos con un estándar riguroso de continuidad operacional.
        </p>
      </section>

      {/* Visión y Misión */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-neutral-900 p-8 rounded-2xl border border-neutral-200 dark:border-neutral-800 space-y-4 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-950/40 text-brand-primary flex items-center justify-center font-bold">
            M
          </div>
          <h3 className="text-xl font-bold text-neutral-900 dark:text-white">Nuestra Misión</h3>
          <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
            Proveer a empresas de retail, logística y gastronomía de equipamiento tecnológico robusto, asesoría de compatibilidad de primer nivel y un soporte post-venta inquebrantable que garantice la atención ininterrumpida a sus clientes finales.
          </p>
        </div>

        <div className="bg-white dark:bg-neutral-900 p-8 rounded-2xl border border-neutral-200 dark:border-neutral-800 space-y-4 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white flex items-center justify-center font-bold">
            V
          </div>
          <h3 className="text-xl font-bold text-neutral-900 dark:text-white">Nuestra Visión</h3>
          <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
            Consolidarnos como el referente técnico y socio estratégico predilecto en Chile para integradores de software e industrias comerciales que demandan hardware de alta confiabilidad y rendimiento.
          </p>
        </div>
      </section>

      {/* Capacidades Técnicas */}
      <section className="space-y-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 dark:text-white">
            Capacidades Operativas e Infraestructura
          </h2>
          <p className="text-xs sm:text-sm text-neutral-500 mt-1">
            Respaldamos cada equipo que comercializamos con recursos técnicos tangibles.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div id="laboratorio" className="p-6 rounded-xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700/60 space-y-3 hover:border-[#33CC00]/50 transition-colors scroll-mt-24">
            <div className="w-10 h-10 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center border border-[#33CC00]/30 shadow-xs">
              <Wrench className="w-5 h-5" />
            </div>
            <h4 className="text-sm font-bold text-neutral-900 dark:text-white">
              Laboratorio Técnico Propio
            </h4>
            <p className="text-xs text-neutral-600 dark:text-neutral-400">
              Banco de pruebas, calibración de cabezales y servicio preventivo directo en Santiago.
            </p>
          </div>

          <div id="cobertura" className="p-6 rounded-xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700/60 space-y-3 hover:border-[#33CC00]/50 transition-colors scroll-mt-24">
            <div className="w-10 h-10 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center border border-[#33CC00]/30 shadow-xs">
              <Truck className="w-5 h-5" />
            </div>
            <h4 className="text-sm font-bold text-neutral-900 dark:text-white">
              Cobertura y Envíos Nacionales
            </h4>
            <p className="text-xs text-neutral-600 dark:text-neutral-400">
              Logística coordinada de despacho para implementaciones a lo largo de todo el territorio nacional.
            </p>
          </div>

          <div id="homologacion" className="p-6 rounded-xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700/60 space-y-3 hover:border-[#33CC00]/50 transition-colors scroll-mt-24">
            <div className="w-10 h-10 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center border border-[#33CC00]/30 shadow-xs">
              <Cpu className="w-5 h-5" />
            </div>
            <h4 className="text-sm font-bold text-neutral-900 dark:text-white">
              Homologación de Drivers POS & SII
            </h4>
            <p className="text-xs text-neutral-600 dark:text-neutral-400">
              Validación con sistemas de boleta electrónica, ERPs y software de caja líder del mercado.
            </p>
          </div>

          <div id="garantia" className="p-6 rounded-xl bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200 dark:border-neutral-700/60 space-y-3 hover:border-[#33CC00]/50 transition-colors scroll-mt-24">
            <div className="w-10 h-10 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center border border-[#33CC00]/30 shadow-xs">
              <Award className="w-5 h-5" />
            </div>
            <h4 className="text-sm font-bold text-neutral-900 dark:text-white">
              Garantía Oficial y Respaldo
            </h4>
            <p className="text-xs text-neutral-600 dark:text-neutral-400">
              Respaldamos formalmente la continuidad de tu negocio con garantías directas de fábrica.
            </p>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="text-center py-12 px-6 rounded-2xl bg-neutral-900 text-white space-y-4">
        <h3 className="text-2xl font-bold">¿Listo para modernizar el equipamiento y hardware de tu negocio?</h3>
        <p className="text-sm text-neutral-400 max-w-xl mx-auto">
          Explora nuestro catálogo en línea o conversa directamente con nuestros especialistas comerciales.
        </p>
        <div className="flex justify-center gap-3 pt-2">
          <Link to="/catalogo">
            <Button variant="primary" size="lg" className="bg-[#33CC00] hover:bg-[#2EB800] text-white border-none shadow-[0_4px_14px_rgba(51,204,0,0.3)] font-bold">
              <span>Explorar Catálogo</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/contacto">
            <Button variant="secondary" size="lg">
              Contactar con un Especialista
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
